import {computed} from "vue";
import {mutate} from "swrv";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {aiAssistantApi} from "@/container";
import {AssistantSettingsPayload} from "@/core/http/resources/AiAssistantResource";
import {
    AssistantConflict,
    AssistantConstraint,
    AssistantParameterField,
    AssistantParameterValues,
    AssistantSettings,
    AssistantSubject
} from "@/ai-assistant/interfaces";

const EMPTY_SETTINGS: AssistantSettings = {
    assistant_key: '',
    schema: [],
    values: {},
    defaults: {},
    typed_constraints: [],
    conflicts: [],
    subjects: []
};

/**
 * The parameters of one assistant for the current pharmacy: the fields the
 * server declares, the values saved against them, and the constraints that
 * carry a guarantee.
 *
 * The form is rendered from `schema` rather than written field by field: part
 * of the list derives from the appointment types each pharmacy configured, so
 * no two officines get the same fields.
 *
 * @param assistantKey
 */
const useAssistantSettings = (assistantKey: string) => {
    const organizationId = useOrganizationId();

    const key = computed(() =>
        organizationId.value
            ? cacheKey(`organizations/${organizationId.value}/ai-assistants/${assistantKey}/settings`)
            : null
    );

    const {data, error, isLoading} = useSwr(
        () => key.value,
        () => organizationId.value
            ? aiAssistantApi().settings(organizationId.value, assistantKey)
            : Promise.resolve(EMPTY_SETTINGS)
    );

    const settings = computed<AssistantSettings>(() => data.value ?? EMPTY_SETTINGS);

    /**
     * A rejected fetch leaves swrv without data, which reads exactly like a
     * pharmacy that has no parameters: without this the panel would present a
     * failure as an empty, editable form.
     */
    const failed = computed<boolean>(() => !!error.value && data.value === undefined);

    const schema = computed<AssistantParameterField[]>(() =>
        [...settings.value.schema].sort((first, second) => (first.order ?? 0) - (second.order ?? 0))
    );

    const values = computed<AssistantParameterValues>(() => settings.value.values);

    const constraints = computed<AssistantConstraint[]>(() => settings.value.typed_constraints);

    const conflicts = computed<AssistantConflict[]>(() => settings.value.conflicts);

    /**
     * The subjects a constraint may name. They travel with the parameters, so
     * they are exactly the ones that produced the caps — the two lists cannot
     * drift apart, and no second request is needed.
     */
    const subjects = computed<AssistantSubject[]>(() => settings.value.subjects);

    /**
     * Show an edit immediately, without waiting for the server.
     *
     * Returned as a whole so a caller can put back exactly what it replaced
     * when the save is refused.
     *
     * @param next
     */
    const write = (next: AssistantSettings) => {
        if (key.value) {
            mutate(key.value, next);
        }
    };

    /**
     * Save the values and the constraints, and hand back what the server kept
     * — including the conflicts it recomputed.
     *
     * Deliberately does not write what it receives: by the time a response
     * lands the pharmacist may have edited again, and writing the server's
     * state over theirs would erase an edit they watched being accepted. The
     * caller knows whether its request is still the current one.
     *
     * Throws on refusal so the caller can restore the state it replaced.
     *
     * @param payload
     */
    const save = async (payload: AssistantSettingsPayload): Promise<AssistantSettings | null> => {
        if (!organizationId.value) {
            return null;
        }

        return aiAssistantApi().saveSettings(organizationId.value, assistantKey, payload);
    };

    /**
     * Drop this pharmacy's values and fall back on the declared defaults.
     */
    const reset = async () => {
        if (!organizationId.value) {
            return;
        }

        write(await aiAssistantApi().resetSettings(organizationId.value, assistantKey));
    };

    return {
        settings,
        schema,
        values,
        constraints,
        conflicts,
        subjects,
        isLoading,
        failed,
        write,
        save,
        reset
    };
};

export default useAssistantSettings;
